import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import useStyles from './index.styles';

import CustomerChatTypeBox from './typeBox';
import CustomerChatHeader from './header';
import CustomerChatBody from './chatBody';
import { useAppDispatch } from 'hooks/useRedux';
import { getChatHistory } from 'store/reducer/customerReducer/action';
import { setChatConnectionId } from 'store/reducer/customerReducer';

export interface ChatType {
  id: string;
  messages: string[];
  timestamp: Date;
  sender: string;
}

interface ChatHistory {
  message: string;
  sent_at: string;
  sent_by: string;
}

const CustomerChatMessenger = ({
  socket,
  isConnected,
  setIsLoading,
  setStartTime,
}: {
  socket: WebSocket | null;
  isConnected: boolean;
  setIsLoading: (x: boolean) => void;
  setStartTime: (x: Date) => void;
}) => {
  const dispatch = useAppDispatch();
  const { id: customerId } = useParams();
  const classes = useStyles();

  const [chats, setChats] = useState<Array<ChatType>>([]);
  const [message, setMessage] = useState<string>('');
  const [isOverADay, setIsOverADay] = useState(false);

  const updateChat = useCallback(
    (message: string, sender: string, timestamp?: Date) => {
      const lastChat = chats.at(-1);
      if (lastChat && lastChat?.sender === sender) {
        let newChat = [...chats];
        newChat[newChat.length - 1] = {
          id: uuidv4(),
          messages: [...lastChat.messages, message],
          timestamp: new Date(),
          sender,
        };
        setChats(newChat);
      } else {
        setChats((prev) => [
          ...prev,
          {
            id: uuidv4(),
            messages: [message],
            timestamp: timestamp ?? new Date(),
            sender,
          },
        ]);
      }
    },
    [chats]
  );

  useEffect(() => {
    if (socket) {
      socket.onmessage = (message: any) => {
        if (message.data) {
          updateChat(JSON.parse(message.data)?.message, 'customer');
          dispatch(setChatConnectionId(JSON.parse(message.data).connection_id));
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  const onSend = () => {
    if (socket && socket.readyState === WebSocket.OPEN && message) {
      let socketMessage =
        chats.length && !isOverADay
          ? message
          : `Agent Janak: ${message}.\n\nThis session will get closed automatically if there is no active message in next 15 minutes.`;

      socket.send(
        JSON.stringify({
          action: 'message',
          message: socketMessage,
        })
      );
      updateChat(message, 'agent');
      setMessage('');
    }
  };

  const fetchChatHistory = useCallback(async () => {
    try {
      const { data } = await dispatch(
        getChatHistory({ customer_id: customerId! })
      ).unwrap();
      if (data.length) {
        const sortedData = data.sort((a: ChatHistory, b: ChatHistory) =>
          moment(a.sent_at).diff(moment(b.sent_at))
        );
        sortedData.forEach((chat: ChatHistory) => {
          updateChat(chat.message, chat.sent_by, moment(chat.sent_at).toDate());
        });

        const lastChatDuration = moment
          .duration(moment().diff(moment(data.at(-1).sent_at)))
          .asHours();
        setIsOverADay(lastChatDuration > 24 ? true : false);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, dispatch]);

  useEffect(() => {
    if (isConnected) fetchChatHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  return (
    <Box sx={{ backgroundColor: '#32475C0A' }}>
      <CustomerChatHeader />

      <Box sx={{ height: 'calc(100vh - 500px)', overflow: 'auto' }}>
        <CustomerChatBody chats={chats} />
      </Box>

      <Box p='1rem'>
        <Box>
          <Typography className={classes.selectTemplate}>
            Select template
          </Typography>
        </Box>

        <CustomerChatTypeBox
          message={message}
          onSend={onSend}
          setMessage={setMessage}
        />
      </Box>
    </Box>
  );
};

export default CustomerChatMessenger;
